import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  CircularProgress,
  IconButton,
  Collapse,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useContext, useEffect, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";

const labelCellSx = {
  fontWeight: 600,
  color: "#444",
  width: "35%",
  fontSize: "0.9rem",
};

export const O365Graph = () => {
  const [state, setState] = useState({
    data: null,
    isLoaded: false,
    error: null,
    mailboxRulesOpen: false,
  });

  const { authState } = useOktaAuth();
  const { userModule } = useContext(UserModuleContext);

  useEffect(() => {
    const fetchGraphInfo = async () => {
      try {
        const response = await fetch(
            `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/o365/mailbox/graph/info`,
            {
              method: "POST",
              headers: {
                Authorization: authState.accessToken.accessToken,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({
                networkId: userModule.userId,
              }),
            }
        );

        if (!response.ok) {
          throw new Error(`User ${userModule.userId} not found in Office 365 APIs`);
        }

        const result = await response.json();
        setState((prev) => ({ ...prev, data: result, isLoaded: true }));
      } catch (error) {
        setState((prev) => ({ ...prev, error, isLoaded: true }));
      }
    };

    fetchGraphInfo();
  }, [authState.accessToken.accessToken, userModule.userId]);

  if (!state.isLoaded) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
        </Box>
    );
  }

  if (state.error) {
    return (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Failed to load Graph info
            </Typography>
            <Typography variant="body2">
              {state.error.message || "Unknown error occurred"}
            </Typography>
          </Box>
        </TableContainer>
    );
  }

  const renderCell = (value) => {
    const display = value !== null && value !== undefined ? value.toString() : null;
    return display ? (
        <Typography variant="body2">{display}</Typography>
    ) : (
        <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>
    );
  };

  return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

        {/* ── Section: License & Access ── */}
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            License &amp; Access
          </Typography>
        </Box>

        <Table>
          <TableBody>
            <TableRow>
              <TableCell sx={labelCellSx}>Microsoft License</TableCell>
              <TableCell>{renderCell(state.data.License)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={labelCellSx}>Block Credential</TableCell>
              <TableCell>{renderCell(state.data.BlockCredential)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Divider sx={{ mx: 3 }} />

        {/* ── Section: Mailbox Rules (collapsible) ── */}
        <Box
            sx={{ px: 3, pt: 3, pb: 1, display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" }}
            onClick={() => setState((prev) => ({ ...prev, mailboxRulesOpen: !prev.mailboxRulesOpen }))}
        >
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5, flex: 1 }}>
            Mailbox Rules
          </Typography>
          <IconButton size="small" sx={{ color: WSU_RED }}>
            {state.mailboxRulesOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </Box>

        <Collapse in={state.mailboxRulesOpen} timeout="auto" unmountOnExit>
          {state.data.MailboxRules?.length > 0 ? (
              <Box sx={{ px: 2, pb: 2, overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["ID", "Display Name", "Sequence", "Enabled", "Has Error", "Read Only", "Conditions", "Actions"].map((col) => (
                          <TableCell
                              key={col}
                              sx={{ fontWeight: 700, color: "#444", fontSize: "0.8rem", whiteSpace: "nowrap" }}
                          >
                            {col}
                          </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {state.data.MailboxRules.map((rule) => (
                        <TableRow key={`mailbox-rule-${rule.id}`}>
                          <TableCell><Typography variant="body2">{rule.id}</Typography></TableCell>
                          <TableCell><Typography variant="body2">{rule.displayName}</Typography></TableCell>
                          <TableCell><Typography variant="body2">{rule.sequence}</Typography></TableCell>
                          <TableCell><Typography variant="body2">{rule.isEnabled.toString()}</Typography></TableCell>
                          <TableCell><Typography variant="body2">{rule.hasError.toString()}</Typography></TableCell>
                          <TableCell><Typography variant="body2">{rule.isReadOnly.toString()}</Typography></TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                              {JSON.stringify(rule.conditions)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                              {JSON.stringify(rule.actions)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
          ) : (
              <Box sx={{ px: 3, py: 1.5, pb: 3 }}>
                <Typography variant="body2" color="text.disabled" fontStyle="italic">
                  No mailbox rules found
                </Typography>
              </Box>
          )}
        </Collapse>

      </TableContainer>
  );
};